import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { 
  ArrowLeft, 
  Search, 
  Plus, 
  LayoutGrid, 
  List, 
  Edit2, 
  Trash2, 
  Package,
  Image as ImageIcon,
  Star,
  Filter,
  Eye,
  EyeOff,
  ShoppingCart,
} from 'lucide-react';
import { useProducts } from '../../hooks/useProducts';
import { useOrders } from '../../hooks/useOrders';
import { AddProductModal } from './AddProductModal';
import { toast } from 'sonner';
import { productService } from '../../services/product.service';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../hooks/useCurrency';
import { useMenuTypes } from '../../hooks/useMenuTypes';
import { useCategories } from '../../hooks/useCategories';
// import { useAuth } from '../../contexts/AuthContext';

interface CategoryProductsPageProps {
  onNavigate?: (page: string) => void;
  categoryId?: string;
  categoryName?: string;
  menuTypeName?: string;
}

export function CategoryProductsPage({ 
  onNavigate, 
  categoryId: propCategoryId, 
  categoryName: propCategoryName,
  menuTypeName: propMenuTypeName
}: CategoryProductsPageProps) {
  // Read from props or localStorage
  const categoryId = propCategoryId || localStorage.getItem('selectedCategoryId') || '';
  const categoryName = propCategoryName || localStorage.getItem('selectedCategoryName') || '';
  const menuTypeName = propMenuTypeName || localStorage.getItem('selectedMenuTypeName') || '';
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterFeatured, setFilterFeatured] = useState<'all' | 'featured' | 'regular'>('all');
  
  // Hooks
  const { products, loading, error, refresh } = useProducts({ 
    categoryId,
    search: searchQuery 
  });
  const { orders: ordersData, total: ordersTotal } = useOrders({ page: 1, limit: 50 });
  const ordersCount = ordersTotal > 0 ? ordersTotal : ordersData.length;
  const { addToCart, getTotalItems } = useCart();
  const { formatCurrency } = useCurrency();
  const { menuTypes } = useMenuTypes();
  const { categories } = useCategories();
  // const { user } = useAuth();

  // Derive active menu type from the actual category (source of truth), fall back to localStorage
  const currentCategory = categories.find(cat => cat.id === categoryId);
  const selectedMenuTypeId = currentCategory?.menuTypeId
    || localStorage.getItem('selectedMenuTypeId')
    || menuTypes.find(mt => mt.name === menuTypeName)?.id
    || '';
  const selectedMenuTypeLabel = menuTypes.find(mt => mt.id === selectedMenuTypeId)?.name
    || menuTypeName
    || '';

  const navigateToSettingsGeneral = () => {
    onNavigate?.('settings-general');
  };

  const navigateToMenuCategories = () => {
    onNavigate?.('settings-menuTypes');
  };

  const navigateToSelectedFoodMenu = () => {
    if (selectedMenuTypeId) {
      localStorage.setItem('settingsFocusMenuTypeId', selectedMenuTypeId);
    }
    if (categoryId) {
      localStorage.setItem('settingsFocusCategoryId', categoryId);
    }

    if (selectedMenuTypeId) {
      onNavigate?.(`settings-menuTypes-${selectedMenuTypeId}`);
      return;
    }
    navigateToMenuCategories();
  };

  const navigateToSelectedCategory = () => {
    if (!categoryId) {
      return;
    }
    localStorage.setItem('selectedCategoryId', categoryId);
    localStorage.setItem('selectedCategoryName', categoryName);
    onNavigate?.(`category-products-${categoryId}`);
  };
  
  // Filter products based on active filters
  const filteredProducts = products.filter(product => {
    if (filterActive === 'active' && !product.isActive) return false;
    if (filterActive === 'inactive' && product.isActive) return false;
    if (filterFeatured === 'featured' && !product.isFeatured) return false;
    if (filterFeatured === 'regular' && product.isFeatured) return false;
    return true;
  });

  const handleDeleteProduct = async (productId: string, productName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"?`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting product:', { productId, productName });
      await productService.delete(productId);
      console.log('✅ Product deleted successfully');
      toast.success('Product deleted successfully!');
      refresh();
    } catch (error: any) {
      console.error('❌ Delete error:', error);
      
      // More detailed error handling
      if (error.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.status === 403) {
        toast.error('You do not have permission to delete products.');
      } else if (error.status === 404) {
        toast.error('Product not found.');
      } else if (error.status === 500 && (
        error.message?.includes('foreign key') || 
        error.message?.includes('constraint') ||
        error.message?.includes('order_items')
      )) {
        // Soft delete migration not run yet
        toast.error('Please run database migration first. See SOFT_DELETE_FIX_SUMMARY.md');
      } else {
        toast.error(error.message || 'Failed to delete product');
      }
    }
  };

  const handleToggleActive = async (productId: string, currentStatus: boolean) => {
    try {
      await productService.toggleAvailability(productId);
      toast.success(`Product ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
      refresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update product status');
    }
  };

  const handleAddToCart = (product: any) => {
    // Map product to MenuItem interface
    const menuItem = {
      id: product.id,
      name: product.name,
      description: product.description || '',
      price: parseFloat(product.price as any) || 0,
      category: categoryId,
      image: product.images && product.images.length > 0 ? product.images[0] : '',
      available: product.isActive,
      popular: product.isFeatured,
    };
    
    addToCart(menuItem);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="settings"
        onNavigate={onNavigate}
        ordersCount={ordersCount}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen}
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Breadcrumb */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <button
                  type="button"
                  onClick={navigateToSettingsGeneral}
                  className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Settings
                </button>
                <span>/</span>
                <button
                  type="button"
                  onClick={navigateToMenuCategories}
                  className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Menu & Categories
                </button>
                {selectedMenuTypeLabel && (
                  <>
                    <span>/</span>
                    <button
                      type="button"
                      onClick={navigateToSelectedFoodMenu}
                      className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                      {selectedMenuTypeLabel}
                    </button>
                  </>
                )}
                <span>/</span>
                <button
                  type="button"
                  onClick={navigateToSelectedCategory}
                  className="text-gray-900 dark:text-white font-medium hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  {categoryName}
                </button>
              </div>
            </div>

            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {categoryName}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    Manage menu items in this category
                  </p>
                </div>
                <button
                  onClick={() => setShowAddProductModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium shadow-lg shadow-indigo-600/30"
                >
                  <Plus className="w-5 h-5" />
                  Add Item
                </button>
              </div>

              {/* Menu Type & Category Navigation */}
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Menu Types Dropdown */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    MENU TYPE
                  </label>
                  <select
                    value={selectedMenuTypeLabel}
                    onChange={(e) => {
                      const selectedMenuType = menuTypes.find(mt => mt.name === e.target.value);
                      if (selectedMenuType) {
                        // Navigate to the first category of this menu type
                        const firstCategory = categories.find(cat => cat.menuTypeId === selectedMenuType.id);
                        if (firstCategory) {
                          localStorage.setItem('selectedMenuTypeId', selectedMenuType.id);
                          localStorage.setItem('selectedMenuTypeName', selectedMenuType.name);
                          localStorage.setItem('selectedCategoryId', firstCategory.id);
                          localStorage.setItem('selectedCategoryName', firstCategory.name);
                          onNavigate?.(`category-products-${firstCategory.id}`);
                        }
                      }
                    }}
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white font-medium"
                  >
                    {menuTypes.map((menuType) => (
                      <option key={menuType.id} value={menuType.name}>
                        {menuType.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Categories Dropdown */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    CATEGORY
                  </label>
                  <select
                    value={categoryId}
                    onChange={(e) => {
                      const selectedCategory = categories.find(cat => cat.id === e.target.value);
                      if (selectedCategory) {
                        localStorage.setItem('selectedCategoryId', selectedCategory.id);
                        localStorage.setItem('selectedCategoryName', selectedCategory.name);
                        onNavigate?.(`category-products-${selectedCategory.id}`);
                      }
                    }}
                    className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white font-medium"
                  >
                    {categories
                      .filter(cat => {
                        // Filter categories by current menu type (derived from actual category data)
                        return selectedMenuTypeId ? cat.menuTypeId === selectedMenuTypeId : true;
                      })
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Search and Filters Bar */}
              <div className="flex items-center gap-3 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    showFilters
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Filter className="w-5 h-5" />
                  Filters
                </button>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <LayoutGrid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded transition-colors ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Filter Options */}
              {showFilters && (
                <div className="mt-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Active Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setFilterActive('all')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterActive === 'all'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setFilterActive('active')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterActive === 'active'
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          Active
                        </button>
                        <button
                          onClick={() => setFilterActive('inactive')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterActive === 'inactive'
                              ? 'bg-gray-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          Inactive
                        </button>
                      </div>
                    </div>

                    {/* Featured Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Featured
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setFilterFeatured('all')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterFeatured === 'all'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          All
                        </button>
                        <button
                          onClick={() => setFilterFeatured('featured')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterFeatured === 'featured'
                              ? 'bg-yellow-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          Featured
                        </button>
                        <button
                          onClick={() => setFilterFeatured('regular')}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filterFeatured === 'regular'
                              ? 'bg-gray-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          Regular
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Count */}
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {filteredProducts.length} of {products.length} items
                </p>
                {(filterActive !== 'all' || filterFeatured !== 'all') && (
                  <button
                    onClick={() => {
                      setFilterActive('all');
                      setFilterFeatured('all');
                    }}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading products...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Package className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {products.length === 0 ? 'No Items Yet' : 'No Matching Items'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {products.length === 0 
                      ? 'Start by adding your first menu item to this category.'
                      : 'Try adjusting your filters or search query.'
                    }
                  </p>
                  {products.length === 0 && (
                    <button
                      onClick={() => setShowAddProductModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                      <Plus className="w-5 h-5" />
                      Add Your First Item
                    </button>
                  )}
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow group"
                  >
                    {/* Product Image */}
                    {product.images && product.images.length > 0 ? (
                      <div className="h-48 bg-gray-200 dark:bg-gray-700 overflow-hidden relative">
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {product.isFeatured && (
                          <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold">
                            <Star className="w-3 h-3" />
                            Featured
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center relative">
                        <ImageIcon className="w-16 h-16 text-gray-400" />
                        {product.isFeatured && (
                          <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold">
                            <Star className="w-3 h-3" />
                            Featured
                          </div>
                        )}
                      </div>
                    )}

                    {/* Product Details */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 dark:text-white truncate flex-1">
                          {product.name}
                        </h4>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                          product.isActive
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      {product.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                          {product.description}
                        </p>
                      )}

                      <div className="text-indigo-600 dark:text-indigo-400 font-bold text-lg mb-4">
                        {formatCurrency(product.price)}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleToggleActive(product.id, product.isActive)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                          title={product.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {product.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => {
                            setEditingProduct(product);
                            setShowAddProductModal(true);
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors text-sm font-medium"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id, product.name)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAddToCart(product)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors text-sm font-medium"
                        >
                          <ShoppingCart className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // List View
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images[0]}
                                alt={product.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {product.name}
                                </p>
                                {product.isFeatured && (
                                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                )}
                              </div>
                              {product.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                  {product.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-white font-mono">
                            {product.sku}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                            {formatCurrency(product.price)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.isActive
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {product.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleActive(product.id, product.isActive)}
                              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title={product.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {product.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => {
                                setEditingProduct(product);
                                setShowAddProductModal(true);
                              }}
                              className="p-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id, product.name)}
                              className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAddToCart(product)}
                              className="p-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => {
          setShowAddProductModal(false);
          setEditingProduct(null);
        }}
        categoryId={categoryId}
        categoryName={categoryName}
        onProductAdded={() => {
          refresh();
        }}
        editProduct={editingProduct}
      />

      {/* Floating Cart Button */}
      {getTotalItems() > 0 && (
        <button
          onClick={() => onNavigate?.('cart')}
          className="fixed bottom-6 right-6 bg-indigo-600 text-white p-4 rounded-full shadow-2xl hover:bg-indigo-700 transition-all duration-300 hover:scale-110 z-50 flex items-center gap-3 group"
        >
          <div className="relative">
            <ShoppingCart className="w-6 h-6" />
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {getTotalItems()}
            </span>
          </div>
          <span className="hidden group-hover:block font-medium">
            View Cart
          </span>
        </button>
      )}
    </div>
  );
}